import React from 'react';
import * as Yup from 'yup';

import { Form } from '@dashboard/components/Formik';
import Link from '@dashboard/components/Link';
import usePage from '@dashboard/hooks/usePage';
import { format } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import timezones from '@dashboard/services/timezones';
import getGmtOffsetForTimezone from '@dashboard/services/getGmtOffsetForTimezone';
import getEventQuantitySoldFromPage from '@repo/checkout/lib/getEventQuantitySoldFromPage';

Yup.setLocale({
  mixed: {
    required: ({ path }) => `${path} is required`,
  },
});

const eventSchema = Yup.object().shape({
  title: Yup.string().min(3, '3 characters minimum').required('Event title is required'),
});

function parseDate(date, timezone) {
  return timezone ? toZonedTime(date, timezone) : new Date(date);
}

const EventDetailsForm = () => {
  const { page, updatePage } = usePage();
  const quantitySoldForEvent = getEventQuantitySoldFromPage(page);

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Form
      initialValues={{
        title: page.title,
        eventDetails: {
          startDate: page.eventDetails.startDate
            ? format(
                parseDate(page.eventDetails.startDate, page.eventDetails.timezone),
                'yyyy-MM-dd HH:mm',
              )
            : undefined,
          endDate: page.eventDetails.endDate
            ? format(
                parseDate(page.eventDetails.endDate, page.eventDetails.timezone),
                'yyyy-MM-dd HH:mm',
              )
            : undefined,
          timezone: page.eventDetails.timezone || userTimezone,
          type: page.eventDetails.type,
          location: page.eventDetails.location,
          mapsLink: page.eventDetails.mapsLink,
          meetingLink: page.eventDetails.meetingLink,
          platform: page.eventDetails.platform,
          capacity: page.eventDetails.capacity === null ? null : page.eventDetails.capacity,
          status: page.eventDetails?.status,
          currency: page.eventDetails.currency,
        },
      }}
      onSubmit={async (values, actions) => {
        const eventDetails = {
          ...page.eventDetails,
          ...values.eventDetails,
          timezone: values.eventDetails.timezone,
        };

        if (values.eventDetails.platform === '') {
          eventDetails.platform = undefined;
        }

        if (values.eventDetails.startDate) {
          eventDetails.startDate = fromZonedTime(
            values.eventDetails.startDate,
            values.eventDetails.timezone,
          );
        } else {
          eventDetails.startDate = undefined;
        }

        if (values.eventDetails.endDate) {
          eventDetails.endDate = fromZonedTime(
            values.eventDetails.endDate,
            values.eventDetails.timezone,
          );
        } else {
          eventDetails.endDate = undefined;
        }

        const { data, error } = await updatePage.mutateAsync({
          title: values.title,
          eventDetails: eventDetails,
          type: 'event',
        });

        actions.resetForm({
          values: {
            title: data.title,
            eventDetails: {
              ...page.eventDetails,
              ...data.eventDetails,
              startDate: data.eventDetails.startDate
                ? format(
                    toZonedTime(data.eventDetails.startDate, data.eventDetails.timezone),
                    'yyyy-MM-dd HH:mm',
                  )
                : undefined,
              endDate: data.eventDetails.endDate
                ? format(
                    toZonedTime(data.eventDetails.endDate, data.eventDetails.timezone),
                    'yyyy-MM-dd HH:mm',
                  )
                : undefined,
              meetingLink: data.eventDetails.meetingLink,
              platform: data.eventDetails.platform,
            },
          },
        });

        return {
          data: data,
          error: error,
        };
      }}
      validationSchema={eventSchema}
      fields={({ values, setFieldValue }) => {
        const isCapacityReached =
          values.eventDetails?.capacity != null &&
          values.eventDetails?.capacity != '' &&
          quantitySoldForEvent >= values.eventDetails?.capacity;

        return [
          {
            name: 'title',
            label: 'Event title',
            type: 'text',
            placeholder: 'Event title',
            autoFocus: true,
            helpText: (
              <Link
                to={`/pages/${page._id}/customize/settings/url-slug`}
                className="!font-normal !text-gray-500 dark:!text-muted-foreground underline hover:text-gray-700 dark:hover:text-muted-foreground"
              >
                <span>/{page.slug}</span>
              </Link>
            ),
            gap: 'sm',
            reduceMargin: true,
          },
          {
            type: 'columns',
            columns: 2,
            fields: [
              {
                name: 'eventDetails.startDate',
                label: 'Start date',
                type: 'datetime-local',
                required: !!values.eventDetails.endDate,
              },
              {
                name: 'eventDetails.endDate',
                label: 'End date',
                type: 'datetime-local',
              },
            ],
            gap: 'sm',
            reduceMargin: true,
          },
          {
            type: 'columns',
            columns: 2,
            fields: [
              {
                name: 'eventDetails.type',
                label: 'Attendance',
                type: 'select',
                options: [
                  {
                    value: '',
                    label: 'Select event type',
                    disabled: true,
                  },
                  { value: 'in_person', label: 'In-person' },
                  { value: 'virtual', label: 'Virtual' },
                  { value: 'hybrid', label: 'Hybrid' },
                ],
                required: true,
              },
              {
                name: 'eventDetails.timezone',
                required: false,
                type: 'select',
                label: 'Timezone',
                tooltip: 'The timezone used for the event and in emails to attendees',
                options: timezones.map(tz => ({
                  value: tz,
                  label: `${tz} (GMT ${getGmtOffsetForTimezone(tz)})`
                    .replace(/_/g, ' ')
                    .replace('/', ' - ')
                    .replace('Etc - UTC', 'UTC'),
                })),
              },
            ],
            reduceMargin: values.eventDetails.type === 'in_person',
            gap: 'sm',
          },
          {
            name: 'eventDetails.location',
            label: 'Location',
            type: 'text',
            visible:
              values.eventDetails.type === 'in_person' || values.eventDetails.type === 'hybrid',
            gap: 'sm',
            reduceMargin: true,
          },
          {
            name: 'eventDetails.mapsLink',
            label: 'Maps link',
            type: 'url',
            gap: 'sm',
            visible:
              values.eventDetails.type === 'in_person' || values.eventDetails.type === 'hybrid',
            reduceMargin: true,
          },
          {
            name: 'eventDetails.platform',
            label: 'Platform',
            type: 'select',
            options: [
              { value: '', label: 'Select platform' },
              { value: 'zoom', label: 'Zoom' },
              { value: 'google_meet', label: 'Google Meet' },
              { value: 'microsoft_teams', label: 'Microsoft Teams' },
              { value: 'youtube', label: 'YouTube' },
              { value: 'hopin', label: 'Hopin' },
              { value: 'vimeo', label: 'Vimeo' },
              { value: 'discord', label: 'Discord' },
              { value: 'other', label: 'Other' },
            ],
            visible:
              values.eventDetails.type === 'virtual' || values.eventDetails.type === 'hybrid',
          },
          {
            name: 'eventDetails.meetingLink',
            label: 'Meeting link',
            type: 'url',
            visible:
              values.eventDetails.type === 'virtual' || values.eventDetails.type === 'hybrid',
            placeholder: 'https://zoom.us/j/...',
          },
          {
            type: 'columns',
            columns: 2,
            fields: [
              {
                name: 'eventDetails.status',
                label: 'Event sales',
                type: 'select',
                options: [
                  {
                    value: 'active',
                    label: 'On sale',
                    disabled: isCapacityReached,
                  },
                  { value: 'sold_out', label: 'Sold out' },
                  { value: 'canceled', label: 'Canceled' },
                ],
              },
              {
                name: 'eventDetails.capacity',
                label: 'Max. capacity',
                type: 'number',
                onChange: e => {
                  setFieldValue('eventDetails.capacity', e.target?.value);
                  if (
                    e.target?.value != null &&
                    e.target?.value != '' &&
                    quantitySoldForEvent >= e.target?.value
                  ) {
                    setFieldValue('eventDetails.status', 'sold_out');
                  }
                },
              },
            ],
            gap: 'sm',
            reduceMargin: true,
          },
          {
            type: 'columns',
            columns: 2,
            fields: [
              {
                name: 'eventDetails.currency',
                label: 'Currency',
                type: 'currency',
              },
            ],
            gap: 'sm',
            reduceMargin: true,
          },
        ];
      }}
    />
  );
};

export default EventDetailsForm;
